/**
 * Loading the org's model into the registry (30.03.06 §4–§7).
 *
 *   loadModel(orgId)    at boot and on login: from cache, or GET /api/model/overlay
 *   refreshModel(orgId) in the background, when a response or a broadcast says
 *                       the version moved; the result waits as `pending`
 *   applyPendingModel() on the next navigation, never mid-screen
 *
 * **The uncustomized path does no request.** The access token carries the
 * org's version as a boot hint (30.03.03 §7). `null` means uncustomized: the
 * registry stays on base and no fetch is made. A hint that names a version
 * found in IndexedDB is used straight from the cache. Only a hint with no cache
 * entry, or a token from before the claim existed, fetches. The hint can be up
 * to an hour stale, so every API response's `X-Model-Version` is compared with
 * the registry, and a disagreement triggers `refreshModel` (§4.1).
 *
 * **A customization failure degrades to the standard product** (§7): a failed
 * fetch, a malformed overlay, a merge that throws, or IndexedDB being
 * unavailable all leave the app on base, logged, and never block it.
 *
 * The client merges its own namespace (30.02 §3.5): the boot fetch returns
 * the client overlays as patches, merged here onto the bundled base with the
 * same `common/` engine the server uses, and the org's txt patches, applied as overrides once a page's translation files are merged.
 */

import { get, set, del, keys } from 'idb-keyval'
import axiosPrivate from '../api/axiosPrivateInstance'
import { applyPatch } from '../../../common/model/patch/index.js'
import { getBaseModel, getModelVersion, setResolvedModel } from './registry'
import { authState } from '../hooks/useAuth'

export const MODEL_HEADER = 'x-model-version'
export const LOCALIZATION_HEADER = 'x-localization'
const PREVIEW_KEY = 'modelPreview'    // sessionStorage: a preview ends with the tab
const BASE = 'base'

const cacheKey = (orgId, localization, version) => `model:${orgId}:${localization ?? BASE}:${version}`

let pending = null        // a fetched model waiting for the next navigation
let inflight = null       // one background refresh at a time
let localization = null   // the last X-Localization seen — part of the cache key (§4.2)

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

/**
 * The boot fetch's body → what the registry holds. A map whose overlay does
 * not apply cleanly stays on base, and says so: one broken overlay must not
 * take the rest of the org's customizations down with it.
 */
export function resolveOverlay(body) {
    const maps = {}
    const problems = []
    for (const [mapName, entry] of Object.entries(body?.client ?? {})) {
        const base = getBaseModel(mapName)
        if (base === undefined) { problems.push({ mapName, message: 'no such model map' }); continue }
        try {
            if (entry?.mode === 'full') { maps[mapName] = entry.patch; continue }
            const r = applyPatch(base, entry?.patch ?? {}, { layerId: 'org', basePath: mapName })
            const errors = r.diagnostics.filter(d => d.severity === 'error')
            if (errors.length) { problems.push({ mapName, diagnostics: errors }); continue }
            maps[mapName] = r.value
        } catch (err) {
            problems.push({ mapName, message: String(err?.message ?? err) })
        }
    }
    return { maps, txt: body?.txt ?? {}, problems }
}

const apply = ({ maps, txt, version }) => setResolvedModel({ maps, txt, version })

// ---------------------------------------------------------------------------
// The token hint
// ---------------------------------------------------------------------------

/** The `modelVersion` claim of the access token: a string, null, or undefined when absent. */
export function tokenHint() {
    try {
        const token = authState.data?.accessToken
        if (!token) return undefined
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
        return 'modelVersion' in payload ? payload.modelVersion : undefined
    } catch {
        return undefined
    }
}

// ---------------------------------------------------------------------------
// Fetch and cache
// ---------------------------------------------------------------------------

async function fetchModel(orgId) {
    const res = await axiosPrivate.get('/api/model/overlay', { validateStatus: s => s === 200 || s === 204 || s === 304 })
    localization = res.headers?.[LOCALIZATION_HEADER] ?? null
    if (res.status === 204) return { version: null, maps: {}, txt: {} }
    const { maps, txt, problems } = resolveOverlay(res.data)
    if (problems.length) console.warn('[model] some overlays did not apply and stay on base:', problems)
    const resolved = { version: res.data.modelVersion, maps, txt }
    try {
        // The patches, not the merge: a merge is only valid against the base it
        // was made from, and the next release ships another. Merging on load
        // costs milliseconds; a stale merge hides every screen added since.
        await set(cacheKey(orgId, localization, resolved.version), { body: { client: res.data.client, txt: res.data.txt }, storedAt: Date.now() })
        await prune(orgId, resolved.version)
    } catch (err) {
        console.warn('[model] IndexedDB unavailable; the model will be fetched on every boot', err)
    }
    return resolved
}

/** Keep the current version and the one before it (rollback lands there); drop the rest. */
async function prune(orgId, current) {
    const mine = (await keys()).filter(k => typeof k === 'string' && k.startsWith(`model:${orgId}:`))
    const previous = getModelVersion()
    for (const k of mine) {
        const v = k.slice(k.lastIndexOf(':') + 1)
        if (v !== current && v !== previous) await del(k)
    }
}

/**
 * Load the org's model into the registry before the app renders (§4). Resolves
 * once the registry holds the right model, or base — never rejects.
 */
export async function loadModel(orgId) {
    if (!orgId) { apply({ version: null }); return }
    const preview = sessionStorage.getItem(PREVIEW_KEY)
    if (preview) axiosPrivate.defaults.headers.common['X-Model-Version'] = preview
    try {
        const hint = preview ? undefined : tokenHint()
        if (hint === null) { apply({ version: null }); return }       // uncustomized: no request
        if (typeof hint === 'string') {
            const cached = await get(cacheKey(orgId, localization, hint)).catch(() => undefined)
            // Merged here, against this build's base: an entry from before the
            // patches were cached (`maps`, no `body`) is fetched again.
            if (cached?.body) {
                const { maps, txt } = resolveOverlay(cached.body)
                apply({ version: hint, maps, txt })
                return
            }
        }
        apply(await fetchModel(orgId))
    } catch (err) {
        console.warn('[model] could not load the org model; using the standard model', err)
        apply({ version: null })
    }
}

// ---------------------------------------------------------------------------
// Background refresh and the next-navigation swap (§4.1, §6)
// ---------------------------------------------------------------------------

/** Fetch the current model in the background; it applies on the next navigation. */
export function refreshModel(orgId) {
    if (!orgId || inflight) return inflight
    inflight = fetchModel(orgId)
        .then(m => { if (m.version !== getModelVersion()) pending = m })
        .catch(err => console.warn('[model] background refresh failed; keeping the current model', err))
        .finally(() => { inflight = null })
    return inflight
}

/** Swap in a pending model. Returns true when it did. */
export function applyPendingModel() {
    if (!pending) return false
    apply(pending)
    pending = null
    return true
}

export const hasPendingModel = () => pending !== null

/**
 * Every response says which version served it. When that disagrees with the
 * registry — a stale token at boot, or a deploy elsewhere — fetch in the
 * background. Installed once.
 */
let watching = false
export function watchModelVersion() {
    if (watching) return
    watching = true
    axiosPrivate.interceptors.response.use((response) => {
        const served = response.headers?.[MODEL_HEADER]
        const orgId = authState.data?.orgId
        if (served && orgId && !String(response.config?.url ?? '').includes('/api/model/overlay')) {
            const v = served === BASE ? null : served
            if (v !== getModelVersion() && v !== pending?.version) refreshModel(orgId)
        }
        return response
    }, (error) => {
        // The preview this tab is in has gone — discarded, or deployed from
        // another tab. The server refuses its version from now on (409, or 403
        // once another preview replaced it), so drop out of the preview rather
        // than failing every request until a reload.
        const code = error?.response?.data?.error
        if (previewVersion() && (code === 'preview-discarded' || code === 'unknown-model-version')) {
            const orgId = authState.data?.orgId
            leavePreview(orgId).catch(() => {})
        }
        return Promise.reject(error)
    })
}

/** A `model.deploy` broadcast: refetch only if the client's namespaces changed (§6). */
export function onModelDeploy(message) {
    const changed = message?.changedNamespaces
    if (Array.isArray(changed) && !changed.some(ns => ns === 'client' || ns === 'txt')) return
    if ((message?.modelVersion ?? null) === getModelVersion()) return
    refreshModel(authState.data?.orgId)
}

// ---------------------------------------------------------------------------
// Preview sessions (§5)
// ---------------------------------------------------------------------------

/** Enter preview: every request names the preview version until the tab closes or it ends. */
export async function enterPreview(orgId, version) {
    sessionStorage.setItem(PREVIEW_KEY, version)
    axiosPrivate.defaults.headers.common['X-Model-Version'] = version
    apply(await fetchModel(orgId))
}

export async function leavePreview(orgId) {
    sessionStorage.removeItem(PREVIEW_KEY)
    delete axiosPrivate.defaults.headers.common['X-Model-Version']
    apply(await fetchModel(orgId).catch(() => ({ version: null })))
}

export const previewVersion = () => sessionStorage.getItem(PREVIEW_KEY)
